// Vendored from the JETCAM DXF Viewer (src/geometry/evaluate.ts), 2026-09-02.
// Origin: C:/inetpub/wwwroot/Jetcost/dxfviewer — that project is untouched.
// Local edits are marked "dxfsplitter:". Do not reformat; keep diffable against origin.

import {
  type ArcEntity,
  type AttribEntity,
  type CircleEntity,
  type DimensionEntity,
  type DxfDocument,
  type DxfEntity,
  type EllipseEntity,
  type EntityBase,
  type HatchEntity,
  type InsertEntity,
  IssueCollector,
  type LineEntity,
  type LwPolylineEntity,
  type MTextEntity,
  type PointEntity,
  type PolylineEntity,
  type PolylineVertex,
  type SolidEntity,
  type SplineEntity,
  type TextLike,
  type Vec3,
} from "../parse/index.ts";
import { bulgeToConic } from "./bulge.ts";
import {
  arcConic,
  asCircularArc,
  circleConic,
  type Conic,
  ellipseConic,
  tessellateConic,
  transformConic,
} from "./conic.ts";
import { boundsOf } from "./extents.ts";
import { tessellateSpline, validateSpline } from "./spline.ts";
import {
  applyPoint,
  applyVector,
  compose,
  IDENTITY,
  length,
  normalize,
  ocsToWcs,
  rotationZ,
  scaling,
  type Transform,
  translation,
} from "./transform.ts";
import {
  type Approximation,
  DEFAULT_CHORD_TOLERANCE,
  DEFAULT_MAX_BLOCK_DEPTH,
  type DisplayList,
  type EntityRef,
  type GeometryOptions,
  type Primitive,
  type PrimitiveBase,
} from "./types.ts";

/** Colour index 0 means BYBLOCK, 256 means BYLAYER. */
const BYBLOCK = 0;
const BYLAYER = 256;
/** What BYBLOCK resolves to when there is no enclosing block. */
const BYBLOCK_AT_TOP_LEVEL = 7;
/** What a BYBLOCK linetype resolves to when there is no enclosing block. */
const BYBLOCK_LINETYPE_AT_TOP_LEVEL = "CONTINUOUS";
const FULL_TURN = Math.PI * 2;
/** Cap on MINSERT array instances, so a corrupt count cannot hang the tab. */
const MAX_ARRAY_INSTANCES = 10_000;

interface Context {
  readonly transform: Transform;
  /** Layer of the enclosing INSERT, for the layer-0 inheritance rule. */
  readonly layer: string | null;
  /** Colour of the enclosing INSERT, for resolving BYBLOCK. */
  readonly color: number | null;
  /** Linetype of the enclosing INSERT, for resolving BYBLOCK. */
  readonly linetype: string | null;
  readonly blockPath: readonly string[];
  readonly instanceIds: readonly number[];
  readonly depth: number;
  /** Block names currently being expanded, for cycle detection. */
  readonly stack: readonly string[];
}

class Evaluator {
  readonly out: Primitive[] = [];
  readonly issues = new IssueCollector();
  readonly skipped: Record<string, number> = {};
  splineCount = 0;
  /** Serial for INSERT expansions, so two placements can be told apart. */
  private nextInstanceId = 0;

  readonly doc: DxfDocument;
  readonly tolerance: number;
  readonly maxDepth: number;

  constructor(doc: DxfDocument, tolerance: number, maxDepth: number) {
    this.doc = doc;
    this.tolerance = tolerance;
    this.maxDepth = maxDepth;
  }

  // -------------------------------------------------------------------------
  // Common attributes
  // -------------------------------------------------------------------------

  private refOf(e: EntityBase): EntityRef {
    return {
      sourceType: e.dxfType,
      srcStart: e.srcStart,
      srcEnd: e.srcEnd,
      ...(e.handle !== undefined ? { handle: e.handle } : {}),
    };
  }

  // dxfsplitter: every `this.out.push({ kind, ...base, transform: t, ... })`
  // below also sets `transform` to the same `t` used to place that push's
  // geometry (excluded here since `baseOf` runs before `t` is chosen, and
  // differs by entity type — see `PrimitiveBase.transform`'s own comment).
  private baseOf(e: EntityBase, ctx: Context): Omit<PrimitiveBase, "kind" | "transform"> {
    // Block geometry drawn on layer "0" takes the layer of the INSERT that
    // placed it. This is AutoCAD's rule, and the Layers tab has to agree with
    // it or a reseller's "hide that layer" will leave geometry on screen.
    const layer = e.layer === "0" && ctx.layer !== null ? ctx.layer : e.layer;

    let color: number;
    if (e.color === undefined) color = BYLAYER;
    else if (e.color === BYBLOCK) color = ctx.color ?? BYBLOCK_AT_TOP_LEVEL;
    else color = e.color;

    let linetype: string;
    if (e.linetype === undefined) linetype = "BYLAYER";
    else if (e.linetype === "BYBLOCK") linetype = ctx.linetype ?? BYBLOCK_LINETYPE_AT_TOP_LEVEL;
    else linetype = e.linetype;

    return {
      ref: this.refOf(e),
      layer,
      color,
      linetype,
      hidden: e.invisible === true,
      blockPath: ctx.blockPath,
      instanceIds: ctx.instanceIds,
      ...(e.trueColor !== undefined ? { trueColor: e.trueColor } : {}),
    };
  }

  /** The transform for an entity whose points are in an OCS. */
  private ocs(e: EntityBase, ctx: Context): Transform {
    return compose(ctx.transform, ocsToWcs(e.extrusion));
  }

  // -------------------------------------------------------------------------
  // Emitters
  // -------------------------------------------------------------------------

  private emitConic(
    conic: Conic,
    t: Transform,
    base: Omit<PrimitiveBase, "kind" | "transform">,
    source: Approximation["source"],
  ): void {
    const world = transformConic(t, conic);
    const arc = asCircularArc(world);

    if (arc) {
      this.out.push({
        kind: "arc",
        ...base,
        transform: t,
        center: arc.center,
        radius: arc.radius,
        startAngle: arc.startAngle,
        endAngle: arc.endAngle,
        ccw: arc.ccw,
      });
      return;
    }

    // Not circular any more: a non-uniform scale, a shear, or a plane no
    // longer parallel to XY. It has to become a polyline.
    const points = tessellateConic(world, this.tolerance);
    const closed = Math.abs(world.end - world.start) >= FULL_TURN - 1e-9;
    if (closed && points.length > 1) points.pop(); // last sample repeats the first

    this.out.push({
      kind: "polyline",
      ...base,
      transform: t,
      points,
      closed,
      approximation: { source, chordTolerance: this.tolerance },
    });
  }

  private emitLine(a: Vec3, b: Vec3, t: Transform, base: Omit<PrimitiveBase, "kind" | "transform">): void {
    this.out.push({ kind: "line", ...base, transform: t, a: applyPoint(t, a), b: applyPoint(t, b) });
  }

  /**
   * A vertex run, which may carry bulges. With no bulges this is one polyline
   * and keeps its closed flag; with bulges it becomes alternating polyline
   * runs and true arcs, because tessellating a bulge would destroy the corner
   * radii and slot ends that make up most of a nesting part.
   */
  private emitVertexRun(
    vertices: readonly PolylineVertex[],
    closed: boolean,
    elevation: number,
    t: Transform,
    base: Omit<PrimitiveBase, "kind" | "transform">,
    useVertexZ: boolean,
  ): void {
    if (vertices.length === 0) return;

    const local = (v: PolylineVertex): Vec3 => ({
      x: v.position.x,
      y: v.position.y,
      z: useVertexZ ? v.position.z : elevation,
    });

    if (vertices.length === 1) {
      this.out.push({ kind: "point", ...base, transform: t, position: applyPoint(t, local(vertices[0]!)) });
      return;
    }

    const segmentCount = closed ? vertices.length : vertices.length - 1;
    const hasBulge = vertices.some((v, i) => {
      if (!closed && i === vertices.length - 1) return false;
      return v.bulge !== undefined && v.bulge !== 0;
    });

    if (!hasBulge) {
      this.out.push({
        kind: "polyline",
        ...base,
        transform: t,
        points: vertices.map((v) => applyPoint(t, local(v))),
        closed,
      });
      return;
    }

    let run: Vec3[] = [local(vertices[0]!)];
    const flush = (): void => {
      if (run.length >= 2) {
        this.out.push({
          kind: "polyline",
          ...base,
          transform: t,
          points: run.map((p) => applyPoint(t, p)),
          closed: false,
        });
      }
      run = [];
    };

    for (let i = 0; i < segmentCount; i += 1) {
      const from = vertices[i]!;
      const to = vertices[(i + 1) % vertices.length]!;
      const a = local(from);
      const b = local(to);
      const conic = bulgeToConic(a, b, from.bulge ?? 0, a.z);

      if (!conic) {
        run.push(b);
        continue;
      }
      flush();
      this.emitConic(conic, t, base, "arc");
      run = [b];
    }
    flush();
  }

  // -------------------------------------------------------------------------
  // Entity dispatch
  // -------------------------------------------------------------------------

  entity(e: DxfEntity, ctx: Context): void {
    const base = this.baseOf(e, ctx);

    switch (e.type) {
      // LINE and POINT store their points in WCS, not in an OCS. Their
      // extrusion only affects thickness, which we do not draw.
      case "LINE": {
        const line = e as LineEntity;
        this.emitLine(line.start, line.end, ctx.transform, base);
        return;
      }
      case "POINT": {
        const point = e as PointEntity;
        this.out.push({
          kind: "point",
          ...base,
          transform: ctx.transform,
          position: applyPoint(ctx.transform, point.position),
        });
        return;
      }

      case "CIRCLE": {
        const c = e as CircleEntity;
        this.emitConic(circleConic(c.center, c.radius), this.ocs(e, ctx), base, "arc");
        return;
      }
      case "ARC": {
        const a = e as ArcEntity;
        this.emitConic(
          arcConic(a.center, a.radius, a.startAngle, a.endAngle),
          this.ocs(e, ctx),
          base,
          "arc",
        );
        return;
      }
      case "ELLIPSE": {
        // ELLIPSE is a WCS entity: its centre and major axis are already in
        // world space, and the extrusion only names the plane it lies in.
        const el = e as EllipseEntity;
        this.emitConic(
          ellipseConic(
            el.center,
            el.majorAxis,
            el.ratio,
            el.startParam,
            el.endParam,
            normalize(el.extrusion),
          ),
          ctx.transform,
          base,
          "ellipse",
        );
        return;
      }

      case "LWPOLYLINE": {
        const p = e as LwPolylineEntity;
        this.emitVertexRun(p.vertices, p.closed, p.elevation ?? 0, this.ocs(e, ctx), base, false);
        return;
      }
      case "POLYLINE": {
        this.polyline(e as PolylineEntity, ctx, base);
        return;
      }
      case "SPLINE": {
        this.spline(e as SplineEntity, ctx, base);
        return;
      }
      case "INSERT": {
        this.insert(e as InsertEntity, ctx);
        return;
      }
      case "SOLID": {
        const s = e as SolidEntity;
        // Group codes 10..13 are a bow tie: the third and fourth corners are
        // swapped relative to a sane quad winding. Reordered here, once.
        const [c0, c1, c2, c3] = s.corners;
        const t = this.ocs(e, ctx);
        this.out.push({
          kind: "polyline",
          ...base,
          transform: t,
          points: [c0, c1, c3, c2].map((p) => applyPoint(t, p)),
          closed: true,
        });
        return;
      }
      case "TEXT":
      case "ATTRIB":
      case "ATTDEF": {
        this.text(e as TextLike, ctx, base);
        return;
      }
      case "MTEXT": {
        this.mtext(e as MTextEntity, ctx, base);
        return;
      }
      case "HATCH": {
        this.hatch(e as HatchEntity, ctx, base);
        return;
      }
      case "DIMENSION": {
        this.dimension(e as DimensionEntity, ctx);
        return;
      }
      default:
        this.skipped[e.dxfType] = (this.skipped[e.dxfType] ?? 0) + 1;
    }
  }

  private polyline(p: PolylineEntity, ctx: Context, base: Omit<PrimitiveBase, "kind" | "transform">): void {
    // Group 70 bit 16 is a polygon mesh and bit 64 a polyface mesh. Neither is
    // profile geometry, and drawing their vertex soup as a polyline would be
    // actively misleading.
    if ((p.flags & 16) !== 0 || (p.flags & 64) !== 0) {
      this.skipped[`${p.dxfType} (mesh)`] = (this.skipped[`${p.dxfType} (mesh)`] ?? 0) + 1;
      this.issues.add(
        "mesh-polyline-skipped",
        "info",
        "A polygon or polyface mesh POLYLINE was not drawn; only profile geometry is shown.",
        { line: p.srcStart, ...(p.handle !== undefined ? { handle: p.handle } : {}) },
      );
      return;
    }

    // Bit 8 marks a 3D polyline, whose vertices are in WCS and carry no bulge.
    const is3d = (p.flags & 8) !== 0;
    const t = is3d ? ctx.transform : this.ocs(p, ctx);
    this.emitVertexRun(p.vertices, p.closed, p.elevation ?? 0, t, base, is3d);
  }

  private spline(s: SplineEntity, ctx: Context, base: Omit<PrimitiveBase, "kind" | "transform">): void {
    this.splineCount += 1;

    // A B-spline is affine-invariant: transforming the control points and then
    // evaluating gives the same curve as evaluating and then transforming, so
    // the tolerance stays meaningful in world units.
    const controlPoints = s.controlPoints.map((p) => applyPoint(ctx.transform, p));
    const input = {
      degree: s.degree,
      controlPoints,
      knots: s.knots,
      weights: s.rational ? s.weights : [],
    };

    const problem = validateSpline(input);
    if (problem === null) {
      this.out.push({
        kind: "polyline",
        ...base,
        transform: ctx.transform,
        points: tessellateSpline(input, this.tolerance),
        closed: s.closed,
        approximation: {
          source: "spline",
          chordTolerance: this.tolerance,
          degree: s.degree,
          controlPointCount: s.controlPoints.length,
          rational: s.rational,
        },
      });
      return;
    }

    // Some writers emit fit points and leave the control points empty. Joining
    // the fit points is a real approximation of the intended curve; joining
    // control points is not, so the two are reported differently.
    if (s.fitPoints.length >= 2) {
      this.issues.add(
        "spline-from-fit-points",
        "info",
        `SPLINE has no usable control points (${problem}); its ${s.fitPoints.length} fit points were joined instead.`,
        { line: s.srcStart, ...(s.handle !== undefined ? { handle: s.handle } : {}) },
      );
      this.out.push({
        kind: "polyline",
        ...base,
        transform: ctx.transform,
        points: s.fitPoints.map((p) => applyPoint(ctx.transform, p)),
        closed: s.closed,
        approximation: { source: "spline-fit-points", chordTolerance: this.tolerance },
      });
      return;
    }

    this.issues.add(
      "spline-not-evaluable",
      "warning",
      `SPLINE could not be evaluated (${problem}); its control polygon is shown instead, which is not the curve.`,
      { line: s.srcStart, ...(s.handle !== undefined ? { handle: s.handle } : {}) },
    );
    if (controlPoints.length >= 2) {
      this.out.push({
        kind: "polyline",
        ...base,
        transform: ctx.transform,
        points: controlPoints,
        closed: s.closed,
        approximation: { source: "spline", chordTolerance: this.tolerance, degree: s.degree },
      });
    }
  }

  private insert(e: InsertEntity, ctx: Context): void {
    const base = this.baseOf(e, ctx);

    // Attributes belong to the INSERT, not to the block: they carry their own
    // absolute positions and are placed with the parent's transform.
    for (const attrib of e.attributes) this.entity(attrib as AttribEntity, ctx);

    const block = this.doc.blocksByName.get(e.blockName);
    if (!block) {
      this.issues.add(
        "missing-block",
        "warning",
        `INSERT references block ${JSON.stringify(e.blockName)}, which the file does not define.`,
        { line: e.srcStart, ...(e.handle !== undefined ? { handle: e.handle } : {}) },
      );
      return;
    }

    if (ctx.stack.includes(e.blockName)) {
      this.issues.add(
        "circular-block-reference",
        "error",
        `Block ${JSON.stringify(e.blockName)} contains an INSERT of itself, directly or through ${ctx.stack.join(" > ")}. Expansion stopped here.`,
        { line: e.srcStart, ...(e.handle !== undefined ? { handle: e.handle } : {}) },
      );
      return;
    }

    if (ctx.depth >= this.maxDepth) {
      this.issues.add(
        "block-nesting-too-deep",
        "error",
        `Block nesting passed ${this.maxDepth} levels at ${JSON.stringify(e.blockName)}; expansion stopped.`,
        { line: e.srcStart, ...(e.handle !== undefined ? { handle: e.handle } : {}) },
      );
      return;
    }

    if (e.scale.x === 0 || e.scale.y === 0 || e.scale.z === 0) {
      this.issues.add(
        "degenerate-insert-scale",
        "warning",
        `INSERT of ${JSON.stringify(e.blockName)} has a zero scale factor, so it collapses to nothing.`,
        { line: e.srcStart, ...(e.handle !== undefined ? { handle: e.handle } : {}) },
      );
    }

    const columns = Math.max(1, Math.trunc(e.columnCount) || 1);
    const rows = Math.max(1, Math.trunc(e.rowCount) || 1);
    let instances = columns * rows;
    if (instances > MAX_ARRAY_INSTANCES) {
      this.issues.add(
        "insert-array-too-large",
        "warning",
        `INSERT of ${JSON.stringify(e.blockName)} declares a ${columns}x${rows} array; only the first ${MAX_ARRAY_INSTANCES} instances were drawn.`,
        { line: e.srcStart, ...(e.handle !== undefined ? { handle: e.handle } : {}) },
      );
      instances = MAX_ARRAY_INSTANCES;
    }

    const ocs = ocsToWcs(e.extrusion); // INSERT stores its point in an OCS
    const childCtx = {
      layer: base.layer,
      color: base.color,
      linetype: base.linetype,
      blockPath: [...ctx.blockPath, e.blockName],
      depth: ctx.depth + 1,
      stack: [...ctx.stack, e.blockName],
    };

    let drawn = 0;
    for (let row = 0; row < rows && drawn < instances; row += 1) {
      for (let col = 0; col < columns && drawn < instances; col += 1) {
        drawn += 1;
        // Every array cell is its own instance, so the Blocks tab can step
        // through a 3x3 MINSERT as nine placements rather than one.
        const instanceIds = [...ctx.instanceIds, this.nextInstanceId++];

        // Read right to left: shift the block's base point to the origin,
        // scale (a negative factor here is a mirror, not an error), step to
        // this array cell, rotate the whole array, then move to the insertion
        // point. Array spacing is in drawing units and is rotated with the
        // block but not scaled with it, which is what AutoCAD does.
        const local = compose(
          translation(e.position),
          compose(
            rotationZ(e.rotation),
            compose(
              translation({ x: col * e.columnSpacing, y: row * e.rowSpacing, z: 0 }),
              compose(
                scaling(e.scale.x, e.scale.y, e.scale.z),
                translation({ x: -block.basePoint.x, y: -block.basePoint.y, z: -block.basePoint.z }),
              ),
            ),
          ),
        );

        const transform = compose(ctx.transform, compose(ocs, local));
        for (const child of block.entities) {
          this.entity(child, { ...childCtx, transform, instanceIds });
        }
      }
    }
  }

  private text(e: TextLike, ctx: Context, base: Omit<PrimitiveBase, "kind" | "transform">): void {
    const t = this.ocs(e, ctx);

    // Group 10 is the insertion point, but any justification other than
    // left/baseline uses the second alignment point instead.
    const justified = (e.halign ?? 0) !== 0 || (e.valign ?? 0) !== 0;
    const anchor = justified && e.alignmentPoint ? e.alignmentPoint : e.position;

    const dir = applyVector(t, { x: Math.cos(e.rotation), y: Math.sin(e.rotation), z: 0 });
    const up = applyVector(t, {
      x: -Math.sin(e.rotation) * e.height,
      y: Math.cos(e.rotation) * e.height,
      z: 0,
    });

    this.out.push({
      kind: "text",
      ...base,
      transform: t,
      text: e.text,
      position: applyPoint(t, anchor),
      height: length(up),
      rotation: Math.atan2(dir.y, dir.x),
      mirrored: t.x.x * t.y.y - t.x.y * t.y.x < 0,
      ...(e.halign !== undefined ? { halign: e.halign } : {}),
      ...(e.valign !== undefined ? { valign: e.valign } : {}),
    });
  }

  private mtext(e: MTextEntity, ctx: Context, base: Omit<PrimitiveBase, "kind" | "transform">): void {
    const t = this.ocs(e, ctx);
    // MTEXT's rotation is already radians in the file, unlike TEXT's.
    const dir = applyVector(t, { x: Math.cos(e.rotation), y: Math.sin(e.rotation), z: 0 });
    const up = applyVector(t, {
      x: -Math.sin(e.rotation) * e.height,
      y: Math.cos(e.rotation) * e.height,
      z: 0,
    });

    this.out.push({
      kind: "text",
      ...base,
      transform: t,
      text: e.text,
      position: applyPoint(t, e.position),
      height: length(up),
      rotation: Math.atan2(dir.y, dir.x),
      mirrored: t.x.x * t.y.y - t.x.y * t.y.x < 0,
      ...(e.attachmentPoint !== undefined ? { attachmentPoint: e.attachmentPoint } : {}),
    });
  }

  private hatch(e: HatchEntity, ctx: Context, base: Omit<PrimitiveBase, "kind" | "transform">): void {
    const t = this.ocs(e, ctx);
    const z = e.elevationPoint.z;
    const at = (p: Vec3): Vec3 => ({ x: p.x, y: p.y, z });

    for (const path of e.boundaryPaths) {
      if (path.isPolyline) {
        this.emitVertexRun(path.vertices, path.closed, z, t, base, false);
        continue;
      }

      for (const edge of path.edges) {
        switch (edge.kind) {
          case "line":
            this.emitLine(at(edge.start), at(edge.end), t, base);
            break;
          case "arc": {
            const conic = arcConic(at(edge.center), edge.radius, edge.startAngle, edge.endAngle);
            this.emitConic(reverseIf(!edge.counterClockwise, conic), t, base, "arc");
            break;
          }
          case "ellipse": {
            const conic = ellipseConic(
              at(edge.center),
              { x: edge.majorAxis.x, y: edge.majorAxis.y, z: 0 },
              edge.ratio,
              edge.startAngle,
              edge.endAngle,
              { x: 0, y: 0, z: 1 },
            );
            this.emitConic(reverseIf(!edge.counterClockwise, conic), t, base, "ellipse");
            break;
          }
          case "spline": {
            const input = {
              degree: edge.degree,
              controlPoints: edge.controlPoints.map((p) => applyPoint(t, at(p))),
              knots: edge.knots,
              weights: edge.rational ? edge.weights : [],
            };
            if (validateSpline(input) === null) {
              this.out.push({
                kind: "polyline",
                ...base,
                transform: t,
                points: tessellateSpline(input, this.tolerance),
                closed: false,
                approximation: { source: "spline", chordTolerance: this.tolerance },
              });
            } else if (input.controlPoints.length >= 2) {
              this.out.push({
                kind: "polyline",
                ...base,
                transform: t,
                points: input.controlPoints,
                closed: false,
              });
            }
            break;
          }
        }
      }
    }
  }

  private dimension(e: DimensionEntity, ctx: Context): void {
    // The dimension itself is not interpreted. What gets drawn is the
    // anonymous block the writer already generated for it, which holds the
    // lines, arrowheads and text as ordinary entities in block space.
    if (e.blockName === undefined) {
      this.skipped[e.dxfType] = (this.skipped[e.dxfType] ?? 0) + 1;
      return;
    }
    const block = this.doc.blocksByName.get(e.blockName);
    if (!block) {
      this.issues.add(
        "missing-dimension-block",
        "info",
        `DIMENSION references block ${JSON.stringify(e.blockName)}, which the file does not define, so it is not drawn.`,
        { line: e.srcStart, ...(e.handle !== undefined ? { handle: e.handle } : {}) },
      );
      return;
    }
    if (ctx.stack.includes(e.blockName) || ctx.depth >= this.maxDepth) return;

    const childCtx: Context = {
      transform: ctx.transform,
      layer: e.layer,
      color: null,
      linetype: null,
      blockPath: [...ctx.blockPath, e.blockName],
      instanceIds: [...ctx.instanceIds, this.nextInstanceId++],
      depth: ctx.depth + 1,
      stack: [...ctx.stack, e.blockName],
    };
    for (const child of block.entities) this.entity(child, childCtx);
  }
}

/** Swap a conic's direction of travel, keeping the same swept set. */
function reverseIf(reverse: boolean, c: Conic): Conic {
  if (!reverse) return c;
  return { ...c, start: c.end, end: c.start };
}

/**
 * Reduce a parsed document to flat world-space geometry.
 *
 * Pure: same document and options in, same display list out, no DOM, no
 * mutation of the input.
 */
export function evaluate(doc: DxfDocument, options: GeometryOptions = {}): DisplayList {
  const tolerance = options.chordTolerance ?? DEFAULT_CHORD_TOLERANCE;
  const maxDepth = options.maxBlockDepth ?? DEFAULT_MAX_BLOCK_DEPTH;
  const ev = new Evaluator(doc, tolerance, maxDepth);

  const root: Context = {
    transform: IDENTITY,
    layer: null,
    color: null,
    linetype: null,
    blockPath: [],
    instanceIds: [],
    depth: 0,
    stack: [],
  };

  for (const e of doc.entities) ev.entity(e, root);

  return {
    primitives: ev.out,
    extents: boundsOf(ev.out),
    issues: ev.issues.all(),
    chordTolerance: tolerance,
    splineCount: ev.splineCount,
    skippedTypes: ev.skipped,
  };
}
